import React, { useState } from 'react';
import { format, parseISO, isValid } from 'date-fns';
import { enUS } from 'date-fns/locale';
import { Calendar as CalendarIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Button } from '@/components/ui/button';

interface DatePickerInputProps {
  value: string;           // ISO string "YYYY-MM-DD" or ''
  onChange: (value: string) => void;
  placeholder?: string;
}

export function DatePickerInput({ value, onChange, placeholder = 'Select date' }: DatePickerInputProps) {
  const [open, setOpen] = useState(false);

  // Parse stored ISO string → Date object
  const selected: Date | undefined = value
    ? (() => { const d = parseISO(value); return isValid(d) ? d : undefined; })()
    : undefined;

  const displayLabel = selected
    ? format(selected, 'MM/dd/yyyy', { locale: enUS })   // American format: 06/10/2026
    : undefined;

  const handleSelect = (day: Date | undefined) => {
    if (day) {
      onChange(format(day, 'yyyy-MM-dd'));   // always store as ISO
    } else {
      onChange('');
    }
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          className={`w-full justify-start text-left font-normal h-8 px-2.5 text-xs border-input bg-background hover:bg-muted/50 ${
            !displayLabel ? 'text-muted-foreground' : 'text-foreground'
          }`}
        >
          <CalendarIcon size={12} className="mr-1.5 opacity-50 flex-shrink-0" />
          {displayLabel ?? placeholder}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-auto p-0 z-50" align="start">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          initialFocus
          locale={enUS}
          // Force English month/weekday display regardless of browser locale
          formatters={{
            formatCaption: (date) => format(date, 'MMMM yyyy', { locale: enUS }),
            formatWeekdayName: (date) => format(date, 'EEE', { locale: enUS }).slice(0, 2),
          }}
        />
        {selected && (
          <div className="p-2 pt-0 border-t border-border">
            <Button
              variant="ghost"
              size="sm"
              className="w-full h-7 text-xs text-muted-foreground"
              onClick={() => { onChange(''); setOpen(false); }}
            >
              Clear date
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
